import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { isChunkLoadError, reloadForFreshChunks } from "@/lib/chunkRecovery";

interface State {
  error: Error | null;
}

/** Shows a recover screen instead of a blank page when rendering crashes. */
export class ChunkErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) reloadForFreshChunks();
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem("gf:module-import-reload");
    } catch {
      /* ignore */
    }
    reloadForFreshChunks();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const isUpdate = isChunkLoadError(this.state.error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="text-xl font-semibold text-foreground">
            {isUpdate ? "A new version is available" : "Something went wrong"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isUpdate
              ? "The app was updated. Reload to get the latest version."
              : "Please reload the page to continue."}
          </p>
          <Button onClick={this.handleReload} className="min-h-[44px] w-full">
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
